import React, { useEffect, useMemo, useRef } from 'react'
import { TableOptions, useFilters, useSortBy, useTable } from 'react-table'
import {
    TableCell,
    TableHeadCell as CoreTableHeadCell,
    TableHeadRow,
    TableRow,
    TableSortLabel,
} from '@/components/detail/DetailTable/DetailTableComponents'
import { useDispatch, useSelector } from 'react-redux'
import { InitialStateType, PageType } from '@/store/reducers'
import { selectInactiveItems, selectActiveItems } from '@/store/selectors'
import TableToolbar from '@/components/detail/DetailTable/TableToolbar/TableToolbar'
import multiSelectFilter from '@/components/detail/DetailTable/filterTypes/multiSelectFilter'
import DetailBlock from '@/components/detail/DetailBlock'
import { FixedSizeList, ListOnScrollProps } from 'react-window'
import styled from 'styled-components'
import { SizeMe } from 'react-sizeme'
import rangeInRangeFilter from '@/components/detail/DetailTable/filterTypes/rangeInRangeFilter'
import {
    filterItemsValues, getVEPCategoryImportance
} from '@/components/detail/DetailTable/TableToolbar/TableFilters/VEPFilter/filterItems'
import { SET_SCROLL_TO_ELEMENT } from '@/store/types'
import includesContainsValue from '@/components/detail/DetailTable/filterTypes/includesContainsValue'

type Props = TableOptions<Record<string, any>> & {
    pageType: PageType
    setVisibleColumns: (value: Array<number>) => void
    setData: (data: any) => void
    csvFileName?: string
}

const filterTypes = {
    multiSelect: multiSelectFilter,
    rangeInRange: rangeInRangeFilter,
    includesContainsValue: includesContainsValue,
}

const alphanumericSort = (rowA: any, rowB: any, columnId: any) => {
    const a = rowA.values[columnId]
    const b = rowB.values[columnId]
    return new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare(a, b)
}

const transcriptConsequenceSort = (rowA: any, rowB: any, columnId: any) => {
    const a = rowA.values[columnId]
    const b = rowB.values[columnId]
    const aImportance = getVEPCategoryImportance(a)
    const bImportance = getVEPCategoryImportance(b)

    if(aImportance === bImportance) {
        return alphanumericSort(rowA, rowB, columnId)
    } else if(aImportance > bImportance) {
        return -1
    } else {
        return 1
    }
}

const sortTypes = {
    alphanumeric: alphanumericSort,
    float: (rowA: any, rowB: any, columnId: any) => {
        const a = parseFloat(rowA.values[columnId])
        const b = parseFloat(rowB.values[columnId])
        return a - b
    },
    transcriptConsequenceSort: transcriptConsequenceSort
}

const DefaultColumn: any = {
    Filter: false,
    Header: <></>,
}

const getColumns = (
    stateColumns: Array<{
        id: string
        active: boolean
    }>,
    columns: Props['columns']
) => {
    return stateColumns.map(stateColumn => {
        return {
            ...columns[Number(stateColumn.id)],
            active: stateColumn.active,
        }
    })
}

const TableHeadCell = styled(CoreTableHeadCell)`
  position: sticky;
  top: 0;
  background-color: ${props => props.theme.palette.white.main};
`

const DetailBlockWrapper = styled(DetailBlock)`
  margin-top: 0;
  padding: 0;
  border-radius: 0 5px 5px 5px;
`

const DetailTableWrapper = styled.div`
  overflow-x: auto;
  padding-bottom: 30px;
`

const FixedSizeListAny = FixedSizeList as unknown as React.FC<any>

const DetailTable = ({
    columns,
    data,
    pageType,
    setVisibleColumns,
    setData,
    csvFileName
}: Props) => {
    const listRef = useRef<any>(null)
    const dispatch = useDispatch()

    const stateColumns = useSelector(
        (state: InitialStateType) => state.detail.columns[pageType]
    )

    const scrollToElement = useSelector(
        (state: InitialStateType) => state.detail.scrollToElement
    )

    const typeColumns = useMemo(() => {
        return getColumns(stateColumns, columns)
    }, [stateColumns])

    const activeStateColumns = useSelector((state: InitialStateType) =>
        selectActiveItems(state, pageType)
    )

    const inactiveColumnsIds = useSelector((state: InitialStateType) =>
        selectInactiveItems(state, pageType)
    ).map(col => col.id)

    const activeColumns = useMemo(() => {
        return getColumns(activeStateColumns, columns)
    }, [activeStateColumns])

    const tableInstance = useTable(
        {
            columns: typeColumns,
            data,
            defaultColumn: DefaultColumn,
            filterTypes,
            sortTypes,
            initialState: {
                hiddenColumns: [
                    ...typeColumns
                        .filter(column => column.show === false)
                        .map(column => (column.id ? column.id : '')),
                    ...inactiveColumnsIds,
                ],
                filters: [
                    {
                        id: '9',
                        value: filterItemsValues,
                    },
                ],
            },
        },
        useFilters,
        useSortBy
    )

    const { headerGroups, rows, prepareRow, state } = tableInstance

    const RenderRow = React.useCallback(
        ({ index, style, data: { columnWidths } }: any) => {
            const row = rows[index]
            prepareRow(row)
            const { key: rowKey, ...getRowProps } = row.getRowProps()
            return (
                <TableRow
                    {...row.getRowProps({
                        style,
                    })}
                    key={row.getRowProps().key}
                >
                    {row.cells.map((cell, i) => {
                        return (
                            <TableCell
                                {...cell.getCellProps()}
                                width={columnWidths[i]}
                                key={i}
                            >
                                <>{cell.render('Cell')}</>
                            </TableCell>
                        )
                    })}
                </TableRow>
            )
        },
        [prepareRow, rows]
    )

    const handleOnScroll = (scroll: ListOnScrollProps) => {
        const visibleRowIndex = scroll.scrollOffset / 16
        const totalHeight = 400
        const numberOfItemsVisible = visibleRowIndex + totalHeight / 16
        setVisibleColumns([
            Math.ceil(visibleRowIndex),
            Math.ceil(numberOfItemsVisible),
        ])
    }

    useEffect(() => {
        setData(rows.map(row => row.original))
    }, [state.filters, state.sortBy])

    useEffect(() => {
        if (scrollToElement) {
            const index = data.findIndex(
                (variant, i) =>
                    data[i + 1] &&
                    scrollToElement >= variant.pos &&
                    scrollToElement <= data[i + 1].pos
            )
            listRef?.current?.scrollToItem(index)
            dispatch(SET_SCROLL_TO_ELEMENT(null))
        }
    }, [scrollToElement])

    return (
        <>
            <TableToolbar
                pageType={pageType}
                activeColumns={activeColumns}
                stateColumns={typeColumns}
                data={data}
                visibleData={rows}
                instance={tableInstance}
                csvFileName={csvFileName}
            />
            <DetailBlockWrapper>
                <DetailTableWrapper>
                    <SizeMe>
                        {({ size }) => {
                            const columns: Array<any> = headerGroups.length
                                ? headerGroups[0].headers
                                : []

                            const availableWidth = size.width ? size.width : 0
                            const minGridWidth = columns.reduce((sum, col) => {
                                return sum + col.minWidth
                            }, 0)

                            const remainingWidth = Math.max(
                                availableWidth - minGridWidth
                                    ? minGridWidth
                                    : 0,
                                0
                            )

                            const totalGrowFactors =
                                columns.reduce(
                                    (sum, col) => sum + col.grow,
                                    0
                                ) || 1
                            const gridWidth = Math.max(
                                availableWidth,
                                minGridWidth
                            )

                            const columnWidths = columns.map(
                                col =>
                                    col.minWidth +
                                    (col.grow / totalGrowFactors) *
                                        remainingWidth
                            )

                            return (
                                <>
                                    {headerGroups.map(headerGroup => {
                                        const {
                                            key: headerGroupKey,
                                            ...getHeaderGroupProps
                                        } = headerGroup.getHeaderGroupProps()
                                        return (
                                            <TableHeadRow
                                                key={headerGroupKey}
                                                {...getHeaderGroupProps}
                                                style={{ width: gridWidth }}
                                            >
                                                {headerGroup.headers.map(
                                                    (column, i) => {
                                                        const {
                                                            key: headerKey,
                                                            ...getHeaderProps
                                                        } =
                                                            column.getHeaderProps()
                                                        const {
                                                            ...columnSortByProps
                                                        } =
                                                            column.getSortByToggleProps()
                                                        return (
                                                            <TableHeadCell
                                                                key={headerKey}
                                                                {...getHeaderProps}
                                                                width={
                                                                    columnWidths[
                                                                        i
                                                                    ]
                                                                }
                                                            >
                                                                <TableSortLabel
                                                                    active={
                                                                        column.isSorted
                                                                    }
                                                                    direction={
                                                                        column.isSortedDesc
                                                                            ? 'desc'
                                                                            : 'asc'
                                                                    }
                                                                    {...columnSortByProps}
                                                                >
                                                                    <>
                                                                        {column.render(
                                                                            'Header'
                                                                        )}
                                                                    </>
                                                                </TableSortLabel>
                                                            </TableHeadCell>
                                                        )
                                                    }
                                                )}
                                            </TableHeadRow>
                                        )
                                    })}
                                    <FixedSizeListAny
                                        ref={listRef}
                                        itemSize={16}
                                        height={rows.length > 0 ? 400 : 0}
                                        itemData={{
                                            columnWidths,
                                        }}
                                        itemCount={rows.length}
                                        width={gridWidth}
                                        onScroll={handleOnScroll}
                                    >
                                        {RenderRow}
                                    </FixedSizeListAny>
                                </>
                            )
                        }}
                    </SizeMe>
                </DetailTableWrapper>
            </DetailBlockWrapper>
        </>
    )
}

export default React.memo(DetailTable)
